'use client'

import React, { useState } from 'react'
import Image from 'next/image'
import { createPortal } from 'react-dom'
import { X, CheckCircle, Clock, Tag, Copy, Check, Infinity, AlertTriangle, ExternalLink, FileText, ChevronDown } from 'lucide-react'
import { toast } from 'react-toastify'
import { discardHTMLTags, getCurrencySymbol, calculateOfferDuration, getFinalDiscountTag, getBaseImageUrl } from '@/constants/hooks'
import { copyToClipboard } from '../utils/clipboard'
import { Offer, ProductData } from '@/services/dataTypes'

interface Props {
    product: Offer | ProductData
    merchantName: string
    merchantLogo: string
    domain: string
    onClose?: () => void
}

const ProductDetailsModalContent = ({ product, merchantName, merchantLogo, domain, onClose }: Props & { onClose: () => void }) => {
    const [copied, setCopied] = useState(false)
    const [revealed, setRevealed] = useState(false)
    const [isExpanded, setIsExpanded] = useState(false)

    const originalPrice = product?.original_price ? parseFloat(product.original_price) : 0
    const salePrice = product?.sale_price ? parseFloat(product.sale_price) : 0
    const discountPct = originalPrice > 0 && salePrice > 0 ? Math.round(((originalPrice - salePrice) / originalPrice) * 100) : null
    const tag = getFinalDiscountTag(product?.offer_title || product?.offer_detail, discountPct)
    const currency = getCurrencySymbol(product?.currency)
    const duration = calculateOfferDuration(product?.end_date ?? null)
    const isCoupon = !!product?.coupon_code
    const imgSrc = product?.offer_type?.name === 'product' && product?.product_image
        ? getBaseImageUrl(domain, product.product_image, '')
        : getBaseImageUrl(domain, merchantLogo, '')

    const handleCopyCode = async () => {
        if (!product?.coupon_code) return
        try {
            await navigator.clipboard.writeText(product.coupon_code)
            setCopied(true)
            setTimeout(() => setCopied(false), 2000)
        } catch {
            try {
                const el = document.createElement('textarea')
                el.value = product.coupon_code
                el.style.position = 'fixed'
                el.style.opacity = '0'
                const container = document.body || document.documentElement
                if (container) {
                    container.appendChild(el)
                    el.focus()
                    el.select()
                    document.execCommand('copy')
                    container.removeChild(el)
                    setCopied(true)
                    setTimeout(() => setCopied(false), 2000)
                }
            } catch {
                toast.error('Failed to copy. Please copy manually.')
            }
        }
    }

    const handleGetDeal = () => {
        const finalUrl = product?.url?.startsWith('/') ? product.url.replace(/^\/+/, '') : (product?.url ?? '')
        window.open(`/${finalUrl}`, '_blank', 'noopener,noreferrer')
    }

    return (
        <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4"
            style={{ willChange: 'transform', isolation: 'isolate', transform: 'translateZ(0)' }}
            onClick={onClose}>
            <div className="absolute inset-0 bg-black/50" />
            <div className="relative w-full max-w-lg max-h-[90vh] flex flex-col rounded-3xl overflow-hidden"
                style={{ background: '#FFFFFF', border: '1px solid rgba(92,122,92,0.15)', boxShadow: '0 24px 60px rgba(45,62,45,0.18)' }}
                onClick={e => e.stopPropagation()}>

                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 flex-shrink-0"
                    style={{ borderBottom: '1px solid rgba(92,122,92,0.1)' }}>
                    <span className="text-xs font-bold uppercase tracking-wider" style={{ color: '#5C7A5C' }}>Product Details</span>
                    <button onClick={onClose}
                        className="w-8 h-8 rounded-full flex items-center justify-center transition-all hover:scale-110"
                        style={{ background: 'rgba(92,122,92,0.08)', color: '#2D3E2D' }}>
                        <X size={15} />
                    </button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">

                    {/* Product header */}
                    <div className="flex items-start gap-4 pb-4" style={{ borderBottom: '1px solid rgba(92,122,92,0.1)' }}>
                        <div className="w-24 h-24 rounded-2xl flex items-center justify-center flex-shrink-0 p-3"
                            style={{ background: '#E8F0E8', border: '1px solid rgba(92,122,92,0.15)' }}>
                            <Image src={imgSrc} alt={merchantName} width={80} height={80} className="object-contain" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <h2 className="text-base font-black leading-snug mb-1.5" style={{ color: '#2D3E2D', fontFamily: 'Georgia, serif' }}>
                                {discardHTMLTags(product?.offer_title)}
                            </h2>
                            <p className="text-xs mb-2" style={{ color: 'rgba(45,62,45,0.45)' }}>
                                by <span className="font-bold" style={{ color: '#2D3E2D' }}>{merchantName}</span>
                            </p>
                            <div className="flex flex-wrap gap-1.5">
                                <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full"
                                    style={{ background: '#E8F0E8', color: '#5C7A5C', border: '1px solid rgba(92,122,92,0.2)' }}>
                                    <CheckCircle size={8} /> Verified
                                </span>
                                {tag && (
                                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full text-white" style={{ background: '#5C7A5C' }}>
                                        {tag}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Pricing */}
                    {(salePrice > 0 || originalPrice > 0) && (
                        <div className="flex items-center gap-3 px-4 py-3 rounded-2xl"
                            style={{ background: '#E8F0E8', border: '1px solid rgba(92,122,92,0.15)' }}>
                            {salePrice > 0 && <span className="text-2xl font-black" style={{ color: '#2D3E2D' }}>{currency}{salePrice.toFixed(2)}</span>}
                            {originalPrice > 0 && salePrice > 0 && (
                                <span className="text-sm line-through" style={{ color: 'rgba(45,62,45,0.4)' }}>{currency}{originalPrice.toFixed(2)}</span>
                            )}
                            {discountPct && (
                                <span className="text-xs font-bold px-2 py-0.5 rounded-full text-white ml-auto" style={{ background: '#5C7A5C' }}>
                                    -{discountPct}%
                                </span>
                            )}
                        </div>
                    )}

                    {/* Duration + end date */}
                    <div className="grid grid-cols-2 gap-3">
                        <div className="px-4 py-3 rounded-2xl" style={{ background: '#FAF7F2', border: '1px solid rgba(92,122,92,0.1)' }}>
                            <p className="text-[9px] font-bold uppercase tracking-wider mb-1" style={{ color: 'rgba(45,62,45,0.35)' }}>Validity</p>
                            <span className="inline-flex items-center gap-1 text-xs font-semibold"
                                style={{ color: duration === 'Offer expired' ? '#F5C542' : '#5C7A5C' }}>
                                {duration === 'Life Time' ? <Infinity size={11} /> : duration === 'Offer expired' ? <AlertTriangle size={11} /> : <Clock size={11} />}
                                {duration}
                            </span>
                        </div>
                        {product?.end_date && (
                            <div className="px-4 py-3 rounded-2xl" style={{ background: '#FAF7F2', border: '1px solid rgba(92,122,92,0.1)' }}>
                                <p className="text-[9px] font-bold uppercase tracking-wider mb-1" style={{ color: 'rgba(45,62,45,0.35)' }}>Expires</p>
                                <span className="text-xs font-semibold" style={{ color: '#2D3E2D' }}>
                                    {calculateOfferDuration(product.end_date)}
                                </span>
                            </div>
                        )}
                    </div>

                    {/* Coupon code matching Theme 5 */}
                    {isCoupon && (
                        <div className="bg-gradient-to-br from-[#E8F0E8]/50 via-[#E8F0E8]/20 to-transparent border border-dashed border-[#5C7A5C]/40 p-4 sm:p-5 rounded-2xl sm:rounded-3xl flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-4">
                            <div className="text-center sm:text-left">
                                <span className="text-[9px] font-black text-[#5C7A5C] uppercase tracking-widest flex items-center gap-1 justify-center sm:justify-start">
                                    <Tag size={10} /> Coupon Code
                                </span>
                                <p className="text-xl sm:text-2xl font-mono font-black text-[#2D3E2D] tracking-wider mt-0.5 sm:mt-1 select-all">
                                    {product.coupon_code}
                                </p>
                            </div>
                            <button
                                onClick={handleCopyCode}
                                className="w-full sm:w-auto px-5 sm:px-6 py-2.5 sm:py-3 bg-[#2D3E2D] hover:bg-[#1E2B1E] text-white text-[10px] font-black uppercase tracking-widest rounded-xl transition-all duration-300 shadow-md active:scale-95 flex items-center justify-center gap-2 cursor-pointer"
                            >
                                {copied ? <Check size={13} /> : <Copy size={13} />}
                                <span>{copied ? 'Copied!' : 'Copy Code'}</span>
                            </button>
                        </div>
                    )}

                    {/* Description */}
                    {product?.offer_detail && (() => {
                        const cleanDetail = discardHTMLTags(product.offer_detail)
                        const isLong = cleanDetail.length > 120
                        return (
                            <div className="pt-2 border-t border-slate-100">
                                <h4 className="text-[11px] font-bold uppercase tracking-wider mb-1 text-[#2D3E2D] flex items-center gap-1.5">
                                    <FileText size={12} className="text-[#5C7A5C]" />
                                    Description
                                </h4>
                                <p className={`text-xs sm:text-sm leading-relaxed text-slate-600 ${isLong && !isExpanded ? 'line-clamp-3' : ''}`}>
                                    {cleanDetail}
                                </p>
                                {isLong && (
                                    <button
                                        type="button"
                                        onClick={() => setIsExpanded(!isExpanded)}
                                        className="mt-1.5 inline-flex items-center gap-1 text-xs font-bold text-[#5C7A5C] hover:underline cursor-pointer"
                                    >
                                        <span>{isExpanded ? 'Read Less' : 'Read More'}</span>
                                        <ChevronDown size={13} className={`transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
                                    </button>
                                )}
                            </div>
                        )
                    })()}

                    {/* CTA */}
                    <button onClick={handleGetDeal}
                        className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl text-sm font-bold text-[#2D3E2D] shadow-sm hover:shadow-md transition-all hover:-translate-y-0.5 cursor-pointer"
                        style={{ background: 'linear-gradient(135deg, #F5C542 0%, #E5B224 100%)' }}>
                        <ExternalLink size={14} /> {isCoupon ? 'Shop Now' : product?.offer_type?.name === 'product' ? 'Buy Now' : 'Get Deal'}
                    </button>
                </div>
            </div>
        </div>
    )
}

const ProductDetailsModal = ({ product, merchantName, merchantLogo, domain, onClose }: Props) => {
    const [mounted, setMounted] = useState(false)
    const [isOpen, setIsOpen] = useState(false)
    React.useEffect(() => { setMounted(true) }, [])

    // Controlled mode — used from OfferDetailsToggle
    if (onClose) {
        if (!mounted) return null
        return createPortal(
            <ProductDetailsModalContent product={product} merchantName={merchantName} merchantLogo={merchantLogo} domain={domain} onClose={onClose} />,
            document.body
        )
    }

    // Self-contained trigger button mode
    return (
        <>
            <button
                onClick={e => { e.preventDefault(); e.stopPropagation(); setIsOpen(true) }}
                className="inline-flex items-center gap-1 text-[10px] font-bold transition-colors hover:opacity-70 cursor-pointer"
                style={{ color: '#5C7A5C' }}>
                View Details
            </button>
            {mounted && isOpen && createPortal(
                <ProductDetailsModalContent
                    product={product} merchantName={merchantName}
                    merchantLogo={merchantLogo} domain={domain}
                    onClose={() => setIsOpen(false)}
                />,
                document.body
            )}
        </>
    )
}

export default ProductDetailsModal
